import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Receipt, AlertTriangle, Search, Ban, RefreshCw, IndianRupee, X, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  getCharges, addCharge, cancelCharge, createDischargeInvoice, recordPayment, getLedger,
  getRoomRentSummary, postRoomRentPending, type RoomRentSummary, type AdmissionLedger,
} from '@/lib/ipdApi';
import { getBillingServices } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { usePermission } from '@/hooks/usePermission';
import { format } from 'date-fns';

// ─── Add Charge Modal ─────────────────────────────────────────────────────────

// Must match backend ENUM: admission_charges.charge_type (schema-mysql.sql)
const CHARGE_TYPES = [
  'room_rent','nursing','consultation','procedure',
  'medicine','investigation','diet','other',
] as const;

interface ChargeForm {
  charge_type: string;
  description: string;
  quantity: string;
  unit_price: string;
  charge_date: string;
}

function emptyCharge(): ChargeForm {
  return {
    charge_type: 'other',
    description: '',
    quantity: '1',
    unit_price: '',
    charge_date: format(new Date(), 'yyyy-MM-dd'),
  };
}

function Sel({ value, onChange, children }: {
  value: string; onChange: (v: string) => void; children: React.ReactNode;
}) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring">
      {children}
    </select>
  );
}

interface AddChargeModalProps {
  admissionId: string;
  admission?: any;
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
}

// Maps billing service category -> IPD charge_type
const SERVICE_CATEGORY_TO_CHARGE_TYPE: Record<string, string> = {
  consultation:  'consultation',
  procedure:     'procedure',
  investigation: 'investigation',
  diagnostic:    'investigation',
  other:         'other',
};

// Charge type → left-border strip colour (solid bg)
const CHARGE_TYPE_BORDER: Record<string, string> = {
  consultation:  'bg-blue-500',
  procedure:     'bg-amber-500',
  investigation: 'bg-emerald-500',
  medicine:      'bg-violet-500',
  nursing:       'bg-sky-500',
  diet:          'bg-orange-500',
  room_rent:     'bg-slate-400',
  other:         'bg-slate-300',
};

// Charge type → subtle colour pill
const CHARGE_TYPE_COLORS: Record<string, string> = {
  consultation:  'bg-primary/10 text-primary',
  procedure:     'bg-warning/10 text-warning',
  investigation: 'bg-success/10 text-success',
  medicine:      'bg-muted text-muted-foreground',
  nursing:       'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  diet:          'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  room_rent:     'bg-muted text-muted-foreground',
  other:         'bg-muted text-muted-foreground',
};

function AddChargeModal({ admissionId, admission, open, onClose, onAdded }: AddChargeModalProps) {
  const { currentUser } = useAuth();
  const [form, setForm] = useState<ChargeForm>(emptyCharge());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [services, setServices] = useState<any[]>([]);
  const [serviceQuery, setServiceQuery] = useState('');
  const [selectedService, setSelectedService] = useState<any | null>(null);
  const [searchFocused, setSearchFocused] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(emptyCharge());
    setError('');
    setServiceQuery('');
    setSelectedService(null);
    setSearchFocused(false);
    if (currentUser?.clinic_id) {
      getBillingServices(currentUser.clinic_id, undefined, undefined, true)
        .then((data: any) => setServices((data ?? []).filter((s: any) => s.is_active)))
        .catch(() => setServices([]));
    }
  }, [open, currentUser?.clinic_id]);

  const set = (k: keyof ChargeForm, v: string) => setForm(f => ({ ...f, [k]: v }));

  const filteredServices = serviceQuery.trim()
    ? services.filter(s => s.name.toLowerCase().includes(serviceQuery.trim().toLowerCase()))
    : services.slice(0, 20);

  const handleSelectService = (svc: any) => {
    setSelectedService(svc);
    setForm(f => ({
      ...f,
      charge_type: svc.ipd_charge_type || SERVICE_CATEGORY_TO_CHARGE_TYPE[svc.category] || 'other',
      description: svc.name,
      unit_price:  String(svc.price),
    }));
    setServiceQuery('');
  };

  const handleClearService = () => {
    setSelectedService(null);
    setForm(emptyCharge());
  };

  const handleSave = async () => {
    setError('');
    if (!form.description.trim()) { setError('Description is required'); return; }
    if (!form.unit_price || isNaN(parseFloat(form.unit_price))) { setError('Valid unit price is required'); return; }
    setSaving(true);
    try {
      await addCharge(admissionId, {
        charge_type: form.charge_type,
        description: form.description,
        quantity:    parseFloat(form.quantity) || 1,
        unit_price:  parseFloat(form.unit_price),
        charge_date: form.charge_date,
        source_type: selectedService ? 'catalog' : 'manual',
      });
      onAdded();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to add charge');
    } finally {
      setSaving(false);
    }
  };

  const total = (parseFloat(form.quantity) || 0) * (parseFloat(form.unit_price) || 0);
  const chargeTypeLabel = (t: string) => t.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md p-0 gap-0">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-[3px] h-7 rounded-full bg-primary shrink-0" />
            <div>
              <p className="text-[9px] font-semibold tracking-[0.15em] uppercase text-muted-foreground leading-none mb-1">IPD Billing</p>
              <h2 className="text-sm font-semibold text-foreground leading-none">Add Charge</h2>
            </div>
          </div>
          {total > 0 && (
            <div className="bg-muted/60 rounded-md px-3 py-1.5 text-right">
              <p className="text-[9px] font-semibold tracking-widest uppercase text-muted-foreground leading-none mb-1">Total</p>
              <p className="font-mono text-base font-bold text-foreground leading-none">₹{total.toLocaleString('en-IN')}</p>
            </div>
          )}
        </div>

        {/* Service picker */}
        <div className="px-5 pt-4 pb-3">
          {!selectedService ? (
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/60 pointer-events-none" />
                <Input
                  value={serviceQuery}
                  onChange={e => setServiceQuery(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
                  placeholder="Search services from catalog…"
                  className="pl-9 h-9 text-sm bg-muted/30 border-border/60 focus:bg-background transition-colors"
                  autoFocus
                />
                {serviceQuery && (
                  <button onClick={() => setServiceQuery('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-muted-foreground/60 hover:text-foreground hover:bg-muted transition-colors">
                    <X className="w-3 h-3" />
                  </button>
                )}

                {(searchFocused || !!serviceQuery.trim()) && filteredServices.length > 0 && (
                  <div className="absolute left-0 right-0 top-full mt-1 z-50 rounded-md border border-border bg-popover shadow-md overflow-hidden max-h-52 overflow-y-auto divide-y divide-border/40">
                    {filteredServices.map(svc => {
                      const typeKey = svc.ipd_charge_type || SERVICE_CATEGORY_TO_CHARGE_TYPE[svc.category] || 'other';
                      return (
                        <button key={svc.id} type="button" onClick={() => handleSelectService(svc)}
                          className="w-full flex items-stretch text-left transition-colors hover:bg-primary/5 group">
                          <div className={cn('w-[3px] shrink-0', CHARGE_TYPE_BORDER[typeKey])} />
                          <div className="flex items-center justify-between px-3 py-2 flex-1 gap-3 min-w-0">
                            <span className="text-sm font-medium text-foreground truncate group-hover:text-primary transition-colors">{svc.name}</span>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className={cn('text-[9px] px-1.5 py-0.5 rounded font-bold tracking-widest uppercase', CHARGE_TYPE_COLORS[typeKey])}>
                                {chargeTypeLabel(typeKey)}
                              </span>
                              <span className="font-mono text-sm font-semibold text-foreground tabular-nums">
                                ₹{Number(svc.price).toLocaleString('en-IN')}
                              </span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <p className="text-[10px] text-muted-foreground/60 font-medium tracking-wide uppercase flex items-center gap-1.5 pl-0.5">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary/40" />
                IPD-applicable services only
              </p>

              {filteredServices.length === 0 && serviceQuery.trim() && services.length > 0 && (
                <p className="text-xs text-muted-foreground py-1 pl-0.5">No matches — fill details below for a manual charge.</p>
              )}
              {services.length === 0 && (
                <p className="text-xs text-muted-foreground py-1 pl-0.5">No IPD services configured. Go to Settings → Billing to add some.</p>
              )}
            </div>
          ) : (
            <div className="flex items-stretch rounded-md border border-primary/25 bg-primary/5 overflow-hidden">
              <div className={cn('w-1 shrink-0', CHARGE_TYPE_BORDER[selectedService.ipd_charge_type || SERVICE_CATEGORY_TO_CHARGE_TYPE[selectedService.category] || 'other'])} />
              <div className="flex items-center gap-2.5 px-3 py-2.5 flex-1 min-w-0">
                <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate leading-none mb-1">{selectedService.name}</p>
                  <span className={cn('text-[9px] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded',
                    CHARGE_TYPE_COLORS[selectedService.ipd_charge_type || SERVICE_CATEGORY_TO_CHARGE_TYPE[selectedService.category] || 'other'])}>
                    {chargeTypeLabel(selectedService.ipd_charge_type || SERVICE_CATEGORY_TO_CHARGE_TYPE[selectedService.category] || 'other')}
                  </span>
                </div>
                <button onClick={handleClearService}
                  className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors shrink-0"
                  title="Clear selection">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="mx-5 border-t border-dashed border-border/50" />

        {/* Form */}
        <div className="px-5 py-4 space-y-3">
          {!selectedService && (
            <>
              <div>
                <label className="block text-[10px] font-bold tracking-[0.12em] uppercase text-muted-foreground mb-1.5">Description *</label>
                <Input value={form.description} onChange={e => set('description', e.target.value)}
                  className="h-8 text-sm" placeholder="e.g. Wound dressing, ECG, Consultant fee" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block text-[10px] font-bold tracking-[0.12em] uppercase text-muted-foreground mb-1.5">Charge Type</label>
                  <Sel value={form.charge_type} onChange={v => set('charge_type', v)}>
                    {CHARGE_TYPES.map(t => (
                      <option key={t} value={t}>{chargeTypeLabel(t)}</option>
                    ))}
                  </Sel>
                </div>
                <div>
                  <label className="block text-[10px] font-bold tracking-[0.12em] uppercase text-muted-foreground mb-1.5">Unit Price ₹</label>
                  <Input type="number" value={form.unit_price} onChange={e => set('unit_price', e.target.value)}
                    className="h-8 text-sm font-mono" min="0" step="0.01" placeholder="0" />
                </div>
              </div>
            </>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[10px] font-bold tracking-[0.12em] uppercase text-muted-foreground mb-1.5">Qty</label>
              <Input type="number" value={form.quantity} onChange={e => set('quantity', e.target.value)}
                className="h-8 text-sm font-mono" min="0.5" step="0.5" />
            </div>
            {selectedService && (
              <div>
                <label className="block text-[10px] font-bold tracking-[0.12em] uppercase text-muted-foreground mb-1.5">Price ₹</label>
                <Input type="number" value={form.unit_price} onChange={e => set('unit_price', e.target.value)}
                  className="h-8 text-sm font-mono" min="0" step="0.01" />
              </div>
            )}
            <div className={selectedService ? '' : 'col-span-2'}>
              <label className="block text-[10px] font-bold tracking-[0.12em] uppercase text-muted-foreground mb-1.5">Date</label>
              <Input type="date" value={form.charge_date}
                min={admission?.admission_date ? format(new Date(admission.admission_date), 'yyyy-MM-dd') : undefined}
                max={format(admission?.discharge_date ? new Date(admission.discharge_date) : new Date(), 'yyyy-MM-dd')}
                onChange={e => set('charge_date', e.target.value)}
                className="h-8 text-sm" />
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded-md px-3 py-2">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />{error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border bg-muted/30 rounded-b-lg flex items-center justify-between gap-3">
          <div className="flex-1 text-xs font-mono text-muted-foreground tabular-nums">
            {total > 0 && (
              <span>
                <span className="opacity-70">{parseFloat(form.quantity) || 0} × ₹{(parseFloat(form.unit_price) || 0).toLocaleString('en-IN')} = </span>
                <span className="font-bold text-foreground">₹{total.toLocaleString('en-IN')}</span>
              </span>
            )}
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button size="sm" className="h-8 text-xs" onClick={handleSave}
              disabled={saving || (!form.description.trim() && !selectedService)}>
              {saving ? 'Adding…' : total > 0 ? `Add — ₹${total.toLocaleString('en-IN')}` : 'Add Charge'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Charges tab ─────────────────────────────────────────────────────────────

export default function IPDChargesTab({ admissionId, admission, canWrite, onChargesChanged }: { admissionId: string; admission?: any; canWrite: boolean; onChargesChanged?: () => void }) {
  const navigate = useNavigate();
  const { can } = usePermission();
  const canViewInvoice = can('billing:read');
  const canGenerateInvoice = can('billing:write');
  const [charges, setCharges] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [invoicing, setInvoicing] = useState(false);
  const [invoiceError, setInvoiceError] = useState('');
  const [cancelTarget, setCancelTarget] = useState<any>(null);
  const [syncOpen, setSyncOpen] = useState(false);
  const [totalBilled, setTotalBilled] = useState<number | null>(null);
  const [roomRent, setRoomRent] = useState<RoomRentSummary | null>(null);
  const [postingRent, setPostingRent] = useState(false);
  const [rentError, setRentError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    getCharges(admissionId, true)
      .then(data => setCharges(data ?? []))
      .catch(console.error)
      .finally(() => setLoading(false));
    getLedger(admissionId)
      .then(l => setTotalBilled(Number(l.total_billed)))
      .catch(console.error);
    getRoomRentSummary(admissionId)
      .then(s => setRoomRent(s))
      .catch(() => setRoomRent(null));
  }, [admissionId]);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    setInvoiceError('');
    setInvoicing(true);
    try {
      const result = await createDischargeInvoice(admissionId);
      navigate(`/billing?invoice=${result.invoice_id}`);
    } catch (e: any) {
      setInvoiceError(e?.message || 'Failed to create invoice');
    } finally {
      setInvoicing(false);
    }
  };

  const handleSync = async () => {
    setInvoiceError('');
    setInvoicing(true);
    try {
      await createDischargeInvoice(admissionId);
      navigate(`/billing?invoice=${admission?.invoice_id}`);
    } catch (e: any) {
      setInvoiceError(e?.message || 'Failed to sync invoice');
    } finally {
      setInvoicing(false);
    }
  };

  const isActive = canWrite;
  const activeCharges = charges.filter(c => !c.is_cancelled);
  const isStale = !!admission?.invoice_id && totalBilled != null
    && Math.abs(Number(admission.invoice_total_amount) - totalBilled) > 0.01;

  const handlePostRent = async () => {
    setRentError('');
    setPostingRent(true);
    try {
      await postRoomRentPending(admissionId);
      load();
      onChargesChanged?.();
    } catch (e: any) {
      setRentError(e?.message || 'Failed to post room rent');
    } finally {
      setPostingRent(false);
    }
  };

  if (loading) return <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>;

  return (
    <div className="space-y-3">

      {/* Room rent summary card */}
      {roomRent && roomRent.rate > 0 && (
        <div className="rounded-lg border border-border bg-muted/20 px-4 py-3 flex items-center justify-between gap-4">
          <div className="space-y-0.5">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Room Rent</p>
            <p className="text-sm text-foreground">
              ₹{roomRent.rate.toLocaleString('en-IN')}/day · {roomRent.total_days} day{roomRent.total_days !== 1 ? 's' : ''}
            </p>
            <p className="text-xs text-muted-foreground">
              Posted: {roomRent.posted_days} day{roomRent.posted_days !== 1 ? 's' : ''}
              {roomRent.pending_days > 0 && (
                <span className="text-warning ml-2">· {roomRent.pending_days} day{roomRent.pending_days !== 1 ? 's' : ''} pending</span>
              )}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            {roomRent.pending_days > 0 && isActive ? (
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={handlePostRent} disabled={postingRent}>
                {postingRent ? 'Posting…' : `Post ₹${roomRent.pending_amount.toLocaleString('en-IN')}`}
              </Button>
            ) : roomRent.pending_days === 0 ? (
              <span className="text-xs text-success font-medium">Up to date ✓</span>
            ) : null}
            {rentError && <p className="text-[10px] text-destructive max-w-[140px] text-right">{rentError}</p>}
          </div>
        </div>
      )}

      {/* Actions bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isActive && (
            <Button size="sm" className="h-8 text-xs" onClick={() => setAddOpen(true)}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Charge
            </Button>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-2">
            {invoiceError && (
              <span className="text-xs text-destructive">{invoiceError}</span>
            )}
            {canGenerateInvoice && (
              <Button
                size="sm" variant="outline" className="h-8 text-xs"
                onClick={() => setPaymentOpen(true)}
                disabled={!admission?.invoice_id}
              >
                <IndianRupee className="w-3.5 h-3.5 mr-1" /> Record Payment
              </Button>
            )}
            {admission?.invoice_id ? (
              <>
                {canViewInvoice && (
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => navigate(`/billing?invoice=${admission.invoice_id}`)}>
                    <Receipt className="w-3.5 h-3.5 mr-1" />
                    View Invoice
                  </Button>
                )}
                {canGenerateInvoice && isStale && (
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setSyncOpen(true)} disabled={invoicing}>
                    <RefreshCw className="w-3.5 h-3.5 mr-1" />
                    Sync Invoice
                  </Button>
                )}
              </>
            ) : (
              canGenerateInvoice && (
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={handleGenerate} disabled={invoicing || activeCharges.length === 0}>
                  <Receipt className="w-3.5 h-3.5 mr-1" />
                  {invoicing ? 'Generating…' : 'Generate Invoice'}
                </Button>
              )
            )}
          </div>
          {!admission?.invoice_id && canGenerateInvoice && (
            <p className="text-[10px] text-muted-foreground/70">Generate invoice first to record payments</p>
          )}
        </div>
      </div>

      {charges.length === 0 ? (
        <div className="text-sm text-muted-foreground py-12 text-center">No charges recorded yet.</div>
      ) : (
        <>
          <div className="rounded-md border overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40">
                  {['Date', 'Type', 'Description', 'Qty', 'Unit Price', 'Total', 'By', ''].map(h => (
                    <th key={h} className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {charges.map((c, i) => (
                  <tr key={c.id} className={cn(
                    'border-b last:border-0',
                    c.is_cancelled ? 'text-muted-foreground line-through' : (i % 2 === 0 ? 'bg-background' : 'bg-muted/20'),
                  )}>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">{format(new Date(c.charge_date), 'dd MMM yyyy')}</td>
                    <td className="px-3 py-2 whitespace-nowrap capitalize">{c.charge_type.replace(/_/g, ' ')}</td>
                    <td className="px-3 py-2">
                      {c.description}
                      {c.is_cancelled && (
                        <span className="block text-xs not-italic">
                          Cancelled{c.cancelled_by_name ? ` by ${c.cancelled_by_name}` : ''}{c.cancel_reason ? ` — ${c.cancel_reason}` : ''}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">{Number(c.quantity)}</td>
                    <td className="px-3 py-2 text-right">₹{Number(c.unit_price).toLocaleString('en-IN')}</td>
                    <td className="px-3 py-2 text-right font-medium">₹{Number(c.total_price).toLocaleString('en-IN')}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">{c.added_by_name}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-right">
                      {!c.is_cancelled && isActive && (
                        <button
                          onClick={() => setCancelTarget(c)}
                          className="text-xs text-destructive hover:underline inline-flex items-center gap-1"
                          aria-label="Cancel charge"
                        >
                          <Ban className="w-3.5 h-3.5" /> Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t bg-muted/40">
                  <td colSpan={5} className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">Total</td>
                  <td className="px-3 py-2 text-right font-semibold">
                    ₹{activeCharges.reduce((s, c) => s + Number(c.total_price), 0).toLocaleString('en-IN')}
                  </td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}

      <AddChargeModal
        admissionId={admissionId}
        admission={admission}
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdded={() => { load(); onChargesChanged?.(); }}
      />

      <CancelChargeModal
        admissionId={admissionId}
        admission={admission}
        charge={cancelTarget}
        open={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onCancelled={() => { load(); onChargesChanged?.(); }}
      />

      <RecordPaymentModal
        admissionId={admissionId}
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
        onRecorded={() => { onChargesChanged?.(); }}
      />

      <Dialog open={syncOpen} onOpenChange={setSyncOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Sync Invoice</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground py-1">
            The invoice was generated before recent charge changes. Syncing will update its line items
            and total ({admission?.invoice_number}) to match the current charges. This cannot be undone.
          </p>
          {invoiceError && (
            <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded px-3 py-2">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />{invoiceError}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSyncOpen(false)} disabled={invoicing}>Cancel</Button>
            <Button onClick={handleSync} disabled={invoicing}>{invoicing ? 'Syncing…' : 'Sync Invoice'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Record Payment Modal ─────────────────────────────────────────────────────

const PAYMENT_MODES = ['cash', 'upi', 'card', 'netbanking', 'cheque', 'advance', 'other'];

interface RecordPaymentModalProps {
  admissionId: string;
  open: boolean;
  onClose: () => void;
  onRecorded: () => void;
}

function RecordPaymentModal({ admissionId, open, onClose, onRecorded }: RecordPaymentModalProps) {
  const [amount, setAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState('cash');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [ledger, setLedger] = useState<AdmissionLedger | null>(null);

  useEffect(() => {
    if (!open) { setLedger(null); return; }
    setAmount('');
    setPaymentMode('cash');
    setReferenceNumber('');
    setNotes('');
    setError('');
    getLedger(admissionId).then(setLedger).catch(() => setLedger(null));
  }, [open, admissionId]);

  const handleSubmit = async () => {
    setError('');
    const amt = parseFloat(amount);
    if (!amount || isNaN(amt) || amt <= 0) { setError('Valid amount is required'); return; }
    setSaving(true);
    try {
      await recordPayment(admissionId, {
        amount: amt,
        payment_mode: paymentMode,
        reference_number: referenceNumber.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      onRecorded();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          {ledger && (
            <div className="grid grid-cols-3 text-xs rounded-md border border-border/60 overflow-hidden">
              <div className="px-3 py-2 border-r border-border/60">
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-0.5">Billed</p>
                <p className="font-mono font-semibold">₹{Number(ledger.total_billed).toLocaleString('en-IN')}</p>
              </div>
              <div className="px-3 py-2 border-r border-border/60">
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-0.5">Paid</p>
                <p className="font-mono font-semibold">₹{Number(ledger.total_paid).toLocaleString('en-IN')}</p>
              </div>
              <div className="px-3 py-2 bg-warning/5">
                <p className="text-[10px] font-bold uppercase tracking-widest text-warning mb-0.5">Due</p>
                <p className="font-mono font-bold text-warning">₹{Number(ledger.balance_due).toLocaleString('en-IN')}</p>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Amount (₹) *</label>
              <Input type="number" value={amount} onChange={e => setAmount(e.target.value)} className="h-8 text-sm" min="0" step="0.01" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Mode</label>
              <Sel value={paymentMode} onChange={setPaymentMode}>
                {PAYMENT_MODES.map(m => (
                  <option key={m} value={m}>{m.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
                ))}
              </Sel>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Reference Number</label>
            <Input value={referenceNumber} onChange={e => setReferenceNumber(e.target.value)} className="h-8 text-sm" placeholder="UTR / cheque no. / txn id" />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Notes</label>
            <Input value={notes} onChange={e => setNotes(e.target.value)} className="h-8 text-sm" placeholder="Optional notes" />
          </div>
          {error && (
            <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded px-3 py-2">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />{error}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : 'Record Payment'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Cancel Charge Modal ──────────────────────────────────────────────────────

interface CancelChargeModalProps {
  admissionId: string;
  admission?: any;
  charge: any;
  open: boolean;
  onClose: () => void;
  onCancelled: () => void;
}

function CancelChargeModal({ admissionId, admission, charge, open, onClose, onCancelled }: CancelChargeModalProps) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setReason('');
    setError('');
  }, [open]);

  const handleSubmit = async () => {
    if (!reason.trim()) { setError('Cancellation reason is required'); return; }
    setSaving(true);
    setError('');
    try {
      await cancelCharge(admissionId, charge.id, reason.trim());
      onCancelled();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to cancel charge');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Cancel Charge</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <p className="text-sm">{charge?.description}</p>
          {admission?.invoice_id && (
            <div className="flex items-start gap-2 text-xs text-warning bg-warning/5 border border-warning/20 rounded-md px-3 py-2">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>
                Invoice {admission.invoice_number ? `#${admission.invoice_number} ` : ''}exists for this admission. Cancelling will make it outdated — use <strong>Sync Invoice</strong> afterwards to update totals.
              </span>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Reason *</label>
            <Input value={reason} onChange={e => setReason(e.target.value)} className="h-8 text-sm" placeholder="Reason for cancellation" />
          </div>
          {error && (
            <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded px-3 py-2">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />{error}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Back</Button>
          <Button variant="destructive" onClick={handleSubmit} disabled={saving}>{saving ? 'Cancelling…' : 'Cancel Charge'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
